import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import type {
  AiKnowledgeBaseEntryDto,
  AiKnowledgeBaseFreshness,
} from '@/shared/api/ai-knowledge-base';
import { Loader } from '@/shared/ui/Loader';

export type KnowledgeBaseFilters = {
  query: string;
  tag: string;
  enabled: '' | 'true' | 'false';
  freshness: '' | AiKnowledgeBaseFreshness;
};

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export function KnowledgeBaseArticleList({
  entries,
  total,
  activeCount,
  selectedId,
  filters,
  isLoading,
  disabled,
  onFiltersChange,
  onSelect,
  onCreate,
}: {
  entries: AiKnowledgeBaseEntryDto[];
  total: number;
  activeCount: number;
  selectedId: string | null;
  filters: KnowledgeBaseFilters;
  isLoading: boolean;
  disabled: boolean;
  onFiltersChange: (filters: KnowledgeBaseFilters) => void;
  onSelect: (id: string) => void;
  onCreate: () => void;
}) {
  const { t } = useTranslation('company');

  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle2" fontWeight={800}>
          {t('modules.knowledgeBase.list.title')}
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={t('modules.knowledgeBase.count', { count: total })}
            sx={{ borderRadius: 1 }}
          />
          <Chip
            size="small"
            label={t('modules.knowledgeBase.activeCount', { count: activeCount })}
            sx={{ borderRadius: 1 }}
            variant="outlined"
          />
        </Stack>
      </Stack>

      <TextField
        size="small"
        label={t('modules.knowledgeBase.search')}
        value={filters.query}
        onChange={(event) =>
          onFiltersChange({ ...filters, query: event.target.value })
        }
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 1,
          },
        }}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1,
        }}
      >
        <TextField
          size="small"
          label={t('modules.knowledgeBase.fields.tags')}
          value={filters.tag}
          onChange={(event) =>
            onFiltersChange({ ...filters, tag: event.target.value })
          }
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
        <TextField
          select
          size="small"
          label={t('modules.knowledgeBase.filters.status')}
          value={filters.enabled}
          onChange={(event) =>
            onFiltersChange({
              ...filters,
              enabled: event.target.value as KnowledgeBaseFilters['enabled'],
            })
          }
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        >
          <MenuItem value="">
            {t('modules.knowledgeBase.filters.allStatuses')}
          </MenuItem>
          <MenuItem value="true">{t('modules.knowledgeBase.enabled')}</MenuItem>
          <MenuItem value="false">{t('modules.knowledgeBase.disabled')}</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label={t('modules.knowledgeBase.filters.freshness')}
          value={filters.freshness}
          onChange={(event) =>
            onFiltersChange({
              ...filters,
              freshness: event.target.value as KnowledgeBaseFilters['freshness'],
            })
          }
          sx={{
            gridColumn: { xs: 'auto', sm: '1 / -1' },
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        >
          <MenuItem value="">
            {t('modules.knowledgeBase.filters.anyFreshness')}
          </MenuItem>
          <MenuItem value="fresh">
            {t('modules.knowledgeBase.freshness.fresh')}
          </MenuItem>
          <MenuItem value="review_due">
            {t('modules.knowledgeBase.freshness.review_due')}
          </MenuItem>
          <MenuItem value="expired">
            {t('modules.knowledgeBase.freshness.expired')}
          </MenuItem>
        </TextField>
      </Box>

      <Button
        variant="contained"
        disabled={disabled}
        onClick={onCreate}
        sx={{
          alignSelf: 'flex-start',
          minHeight: 36,
          borderRadius: 1,
          textTransform: 'none',
          fontWeight: 800,
        }}
      >
        {t('modules.knowledgeBase.createNew')}
      </Button>

      {isLoading ? (
        <Loader containerSize="small" />
      ) : entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('modules.knowledgeBase.empty')}
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {entries.map((entry) => {
            const selected = entry.id === selectedId;

            return (
              <Button
                key={entry.id}
                variant="outlined"
                onClick={() => onSelect(entry.id)}
                sx={{
                  display: 'block',
                  width: '100%',
                  p: 1,
                  borderRadius: 1,
                  textTransform: 'none',
                  textAlign: 'left',
                  borderColor: (theme) =>
                    selected
                      ? theme.palette.primary.main
                      : alpha(theme.palette.text.primary, 0.14),
                  bgcolor: (theme) =>
                    selected
                      ? alpha(theme.palette.primary.main, 0.08)
                      : 'background.paper',
                }}
              >
                <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    color="text.primary"
                    noWrap
                  >
                    {entry.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(entry.updated_at)}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={
                        entry.enabled
                          ? t('modules.knowledgeBase.enabled')
                          : t('modules.knowledgeBase.disabled')
                      }
                      sx={{ borderRadius: 1 }}
                    />
                    <Chip
                      size="small"
                      label={t(`modules.knowledgeBase.freshness.${entry.freshness}`)}
                      sx={{ borderRadius: 1 }}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`v${entry.version}`}
                      sx={{ borderRadius: 1 }}
                      variant="outlined"
                    />
                    {entry.tags.map((tag) => (
                      <Chip
                        key={tag}
                        size="small"
                        label={tag}
                        sx={{ borderRadius: 1 }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Button>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
